import { Component, Input, Output, EventEmitter, OnChanges, inject } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { ProjectService } from '../../services/project.service';
import { ProjectPanelService } from '../../services/project-panel.service';
import { ProjectSelectionService } from '../../services/project-selection.service';
import { ProjectsDataDto } from '../../models/dashboard.dto';

@Component({
  selector: 'app-project-selector',
  standalone: true,
  imports: [LucideAngularModule],
  templateUrl: './project-selector.component.html',
  styleUrl: './project-selector.component.scss',
})
export class ProjectSelectorComponent implements OnChanges {
  readonly projectService = inject(ProjectService);
  private readonly projectPanelService = inject(ProjectPanelService);
  private readonly projectSelection = inject(ProjectSelectionService);

  @Input() projects: ProjectsDataDto[] = [];
  @Output() mobileClose = new EventEmitter<void>();

  open = false;
  selectedProjectIds = new Set<string>();
  pinnedProjectIds = new Set<string>();

  private idsRestored = false;
  private restoreInFlight = false;

  ngOnChanges(): void {
    // `projects` arrives asynchronously from the parent's data fetch, so the persisted
    // ids (which must be validated against real project ids) can only be loaded once
    // the list is actually populated — and only once, so later re-fetches don't clobber
    // selections/pins made during this session.
    if (this.idsRestored || this.restoreInFlight || this.projects.length === 0) {
      return;
    }
    // The persisted sets are keyed by user id, so they are not readable until the session has
    // settled. `restored` stays false when the session settled without an account, so the next
    // time `projects` arrives — which is what a successful session retry produces — this tries
    // again rather than latching the default selection over a choice it never got to read.
    this.restoreInFlight = true;
    void this.projectSelection.whenReady().then((hasAccount) => {
      this.restoreInFlight = false;
      if (!hasAccount) return;
      this.idsRestored = true;
      this.restoreIds();
    });
  }

  get sortedProjects(): ProjectsDataDto[] {
    const pinned = this.projects.filter(p => this.pinnedProjectIds.has(p.id));
    const rest = this.projects.filter(p => !this.pinnedProjectIds.has(p.id));
    return [...pinned, ...rest];
  }

  get selectedProjects(): ProjectsDataDto[] {
    return this.projects.filter(p => this.selectedProjectIds.has(p.id));
  }

  isSelected(project: ProjectsDataDto): boolean {
    return this.selectedProjectIds.has(project.id);
  }

  isPinned(project: ProjectsDataDto): boolean {
    return this.pinnedProjectIds.has(project.id);
  }

  toggleOpen(): void {
    this.open = !this.open;
  }

  close(): void {
    this.open = false;
  }

  toggleSelection(project: ProjectsDataDto, event: Event): void {
    event.stopPropagation();
    if (this.selectedProjectIds.has(project.id)) {
      this.selectedProjectIds.delete(project.id);
    } else {
      this.selectedProjectIds.add(project.id);
    }
    this.projectSelection.setSelectedProjectIds(this.selectedProjectIds);

    if (this.selectedProjectIds.size === 1) {
      const onlySelected = this.projects.find(p => this.selectedProjectIds.has(p.id));
      if (onlySelected) {
        this.setActiveProject(onlySelected);
      }
    }
  }

  removeSelection(projectId: string, event: Event): void {
    event.stopPropagation();
    this.selectedProjectIds.delete(projectId);
    this.projectSelection.setSelectedProjectIds(this.selectedProjectIds);
  }

  togglePin(project: ProjectsDataDto, event: Event): void {
    event.stopPropagation();
    if (this.pinnedProjectIds.has(project.id)) {
      this.pinnedProjectIds.delete(project.id);
    } else {
      this.pinnedProjectIds.add(project.id);
    }
    this.projectSelection.setPinnedProjectIds(this.pinnedProjectIds);
  }

  openCreateProject(): void {
    this.projectPanelService.openCreatePanel();
    this.close();
  }

  getInitials(name: string): string {
    return name
      .split(' ')
      .map(w => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  private restoreIds(): void {
    this.selectedProjectIds = this.knownOnly(this.projectSelection.selectedProjectIds());
    this.pinnedProjectIds = this.knownOnly(this.projectSelection.pinnedProjectIds());

    // Mirror the layout's default project selection so the selector doesn't render
    // empty on first load when nothing has been picked yet. Prefer whatever project
    // is already active (e.g. auto-selected by the layout, or deep-linked) over
    // defaulting to the first one, so the two stay in sync.
    if (this.selectedProjectIds.size === 0 && this.projects.length > 0) {
      const activeProject = this.projects.find(p => p.id === this.projectService.projectId);
      const defaultProject = activeProject ?? this.projects[0];
      this.selectedProjectIds.add(defaultProject.id);
      if (!activeProject) {
        this.setActiveProject(defaultProject);
      }
    }

    this.projectSelection.setSelectedProjectIds(this.selectedProjectIds);
  }

  private setActiveProject(project: ProjectsDataDto): void {
    this.projectService.setProject({
      id: project.id,
      name: project.name,
      description: project.description,
      color: project.color,
      logo: project.logo,
    });
  }

  /** Persisted ids outlive the projects they name — a deleted project, or another org's. */
  private knownOnly(ids: ReadonlySet<string>): Set<string> {
    return new Set([...ids].filter(id => this.projects.some(p => p.id === id)));
  }
}
